import { ShoppingCart, Package, Briefcase } from "@phosphor-icons/react";

interface ServiceCardProps {
	icon: React.ReactNode;
	title: string;
	description: string;
	tags: string[];
	bgColor: string;
	iconBg: string;
	illustration: string;
	premium?: boolean;
	comingSoon?: boolean;
	className?: string;
}

function ServiceCard({
	icon,
	title,
	description,
	tags,
	bgColor,
	iconBg,
	illustration,
	premium,
	comingSoon,
	className = "",
}: ServiceCardProps) {
	return (
		<div
			className={`relative flex flex-col border-4 border-[#0F172A] rounded-[20px] overflow-hidden h-[520px] md:h-[560px] ${className}`}
			style={{ backgroundColor: bgColor }}
		>
			{/* Premium badge */}
			{premium && (
				<div className="absolute top-5 right-5 z-20 bg-[#0F172A] text-white text-[11px] font-bold px-3 py-1 rounded-full uppercase tracking-widest">
					Premium
				</div>
			)}

			{/* Coming Soon sash */}
			{comingSoon && (
				<div className="absolute -right-[34px] top-[30px] z-20 rotate-45 bg-[#0F172A] text-white text-[10px] font-bold py-1 px-10 uppercase tracking-widest shadow-md">
					Coming Soon
				</div>
			)}

			{/* Top content */}
			<div className="px-7 pt-7 pb-0 relative z-10 flex-shrink-0">
				{/* Icon */}
				<div
					className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 text-white"
					style={{ backgroundColor: iconBg }}
				>
					{icon}
				</div>

				{/* Title */}
				<h4 className="text-[26px] font-extrabold text-[#0F172A] leading-tight mb-3">
					{title}
				</h4>

				{/* Description */}
				<p className="text-[15px] text-[#334155] leading-relaxed">
					{description}
				</p>
			</div>

			{/* Tag row */}
			<div className="px-7 pt-5 flex flex-wrap gap-2 relative z-10 flex-shrink-0">
				{tags.map((tag) => (
					<span
						key={tag}
						className="text-xs font-semibold px-3 py-1.5 rounded-full border-2 border-[#0F172A] bg-white/70 text-[#0F172A]"
					>
						{tag}
					</span>
				))}
			</div>

			{/* Illustration — bleeds up from bottom, white bg erased via multiply */}
			<div
				className="absolute bottom-0 left-0 right-0 h-[260px] pointer-events-none"
				style={{
					maskImage: "linear-gradient(to bottom, transparent 0%, black 30%)",
				}}
			>
				<img
					src={illustration}
					alt=""
					aria-hidden="true"
					className="w-full h-full object-cover object-top"
					style={{ mixBlendMode: "multiply" }}
				/>
			</div>
		</div>
	);
}

const CARDS: ServiceCardProps[] = [
	{
		icon: <ShoppingCart size={26} weight="fill" />,
		title: "Standard Errands",
		description:
			"Post the task, get bids from runners nearby, and chat to settle the price. Funds sit in escrow until you confirm the job is done.",
		tags: ["Shopping", "Parcel", "Household", "Custom"],
		bgColor: "#FFEDB3",
		iconBg: "#F59E0B",
		illustration: "/illustrations/standard-errands.png",
	},
	{
		icon: <Package size={26} weight="fill" />,
		title: "PROXI Direct",
		description:
			"Same-day parcel delivery with upfront pricing. Pick a size, see the price, and a rider is on the way — no back-and-forth.",
		tags: ["Fixed Pricing", "Instant Dispatch", "A-to-B Routing", "Same Day"],
		bgColor: "#B3D9FF",
		iconBg: "#3E54D3",
		illustration: "/illustrations/proxi-direct.png",
		comingSoon: true,
		className: "md:mt-12",
	},
	{
		icon: <Briefcase size={26} weight="fill" />,
		title: "Proxi Private",
		description:
			"A background-checked concierge for sensitive errands. NDA-signed, insured, and discreet — built for legal papers and high-value pickups.",
		tags: ["NDA Protected", "Background Checked", "Insured", "Confidential"],
		bgColor: "#DCD6F9",
		iconBg: "#7C3AED",
		illustration: "/illustrations/proxi-private.png",
	},
];

export function Services() {
	return (
		<section id="services" className="py-24 lg:py-32 bg-white px-4">
			<div className="max-w-7xl mx-auto">
				{/* Header */}
				<div className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
					<p className="text-secondary font-bold tracking-widest uppercase text-sm mb-4">
						Three Ways to Use Proxi
					</p>
					<h2 className="text-3xl md:text-4xl font-extrabold text-dark mb-6">
						Pick the service <br className="hidden sm:block" />
						<span className="font-script text-primary text-4xl md:text-5xl font-normal">
							that fits your errand.
						</span>
					</h2>
					<p className="text-lg text-slate">
						Whether it's a quick grocery run or a confidential document drop,
						every Proxi task is covered by the same escrow, private chat, and
						verified-runner layer.
					</p>
				</div>

				{/* Cards */}
				<div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
					{CARDS.map((card) => (
						<ServiceCard key={card.title} {...card} />
					))}
				</div>
			</div>
		</section>
	);
}
